import { inject, injectable } from 'inversify'
import { makeObservable, observable, runInAction } from 'mobx'
import { HttpGateway } from '@core/services/http.gateway'
import { Types } from '@core/ioc/types'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import { MessagePacking, type ActionFailurePm, type ActionResponsePm } from '@core/feedback/feedback.packing'
import { mapReadFailure } from '@core/services/gateway-failure.helpers'
import { z } from '@core/validation/validation.schema'
import { OperationRepository } from '@core/operations/operation.repository'
import type {
  OperationHandle,
  OperationOwner,
  OperationResult,
  ReadonlyOperationState,
} from '@core/operations/operation.types'
import {
  defineOperation,
  operationFailed,
  operationGroup,
  operationId,
  operationKey,
  operationSucceeded,
} from '@core/operations/operation.helpers'
import { FreshnessRepository } from '@core/infra/freshness/freshness.repository'
import { OfflineCacheRepository } from '@core/infra/offline/offline-cache.repository'

/** ActionFailurePm plus the retryability signal `books.load`'s retry policy needs — still assignable back into plain ActionFailurePm for the external ActionResponsePm contract. */
export type LoadBooksFailure = ActionFailurePm & Readonly<{ retryable: boolean }>

export type BookPm = {
  readonly id: string
  readonly name: string
  /** False when this row reaches the viewer through an access grant rather than ownership. */
  readonly own: boolean
  /** The owning principal's display form: an email, or a team's name. */
  readonly ownerLabel: string
  readonly inventoryName: string
}

export const BookListDtoSchema = z.array(
  z.object({
    bookId: z.string(),
    name: z.string(),
    ownerLabel: z.string(),
    inventoryName: z.string(),
    own: z.boolean(),
  }),
)

const toBookPm = (dto: z.infer<typeof BookListDtoSchema>[number]): BookPm =>
  Object.freeze({
    id: dto.bookId,
    name: dto.name,
    own: dto.own,
    ownerLabel: dto.ownerLabel,
    inventoryName: dto.inventoryName,
  })

export const LOAD_BOOKS_OPERATION = defineOperation<null, LoadBooksFailure>({
  id: operationId('books.load'),
  key: () => operationKey('books'),
  groups: () => [operationGroup('session'), operationGroup('books')],
  pending: { kind: 'join' },
  safety: { kind: 'read' },
  timeout: {
    kind: 'after',
    milliseconds: 15_000,
    toFailure: () => ({ code: 'temporarily-unavailable', retryable: false }),
  },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

/**
 * Single source of truth for the books list — holds the Programmers Model
 * as observable state (machine-architecture.md §3.2/§7). BooksPagePresenter only
 * derives a ViewModel from this, it does not own or fetch data itself.
 */
@injectable()
export class BooksRepository implements UserScopedState {
  bookListPm: readonly BookPm[] | null = null

  private readonly dataGateway: HttpGateway
  private readonly freshnessRepository: FreshnessRepository
  private readonly offlineCache: OfflineCacheRepository
  private readonly operationOwner: OperationOwner
  private readonly loadOperation: OperationHandle<null, LoadBooksFailure>

  constructor(
    @inject(Types.IDataGateway) dataGateway: HttpGateway,
    @inject(OperationRepository) operationRepository: OperationRepository,
    @inject(FreshnessRepository) freshnessRepository: FreshnessRepository,
    @inject(OfflineCacheRepository) offlineCache: OfflineCacheRepository,
  ) {
    this.dataGateway = dataGateway
    this.freshnessRepository = freshnessRepository
    this.offlineCache = offlineCache
    this.operationOwner = operationRepository.createOwner({ id: 'books.repository', lifetime: 'session' })
    this.loadOperation = this.operationOwner.register(LOAD_BOOKS_OPERATION)

    makeObservable(this, {
      bookListPm: observable.ref,
    })
  }

  get isLoaded(): boolean {
    return this.bookListPm !== null
  }

  // Route-entry preload target (RoutesConfig): reuse what is already in
  // memory rather than re-fetching behind a skeleton on every visit. Pure
  // Repository policy — no feedback decision involved, so it belongs here,
  // not on a Presenter (P-40: only a Presenter writes feedback).
  loadIfNeeded = async (): Promise<void> => {
    if (this.isLoaded && this.freshnessRepository.isFresh('books')) return
    await this.load()
  }

  get loadState(): ReadonlyOperationState<LoadBooksFailure> {
    return this.loadOperation.stateFor(null)
  }

  load = async (): Promise<ActionResponsePm> => {
    const result = await this.loadOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.get('/books', { signal, idempotencyKey: null })
        if (!response.success) {
          const cached = this.offlineCache.readOnNetworkFailure('books', BookListDtoSchema, response.failure)
          if (cached) return operationSucceeded(Object.freeze(cached.map(toBookPm)))
          return operationFailed(mapReadFailure(response))
        }

        const parsed = BookListDtoSchema.safeParse(response.result)
        if (!parsed.success) {
          return operationFailed({ code: 'invalid-external-data', retryable: false })
        }

        this.offlineCache.write('books', parsed.data)
        const books = Object.freeze(parsed.data.map(toBookPm))
        return operationSucceeded(books)
      },
      {
        succeeded: (books) => {
          this.bookListPm = books
          if (!this.offlineCache.wasServedFromCache('books')) this.freshnessRepository.markLoaded('books')
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  // Registered against Types.IUserScopedState — logout resets it without
  // AuthenticationRepository having to know this class exists. Cancel before
  // clear (spec §16): a pre-logout load settling afterward must not
  // repopulate this Singleton with the previous user's data.
  resetUserScopedState = (): void => {
    this.operationOwner.resetAll({ kind: 'session-reset' })
    runInAction(() => {
      this.bookListPm = null
    })
  }

  private toActionResponsePm(result: OperationResult<unknown, ActionFailurePm>): ActionResponsePm {
    switch (result.kind) {
      case 'succeeded':
        return MessagePacking.succeeded()
      case 'failed':
        return { success: false, failure: result.error }
      case 'cancelled':
      case 'skipped':
        return MessagePacking.cancelled()
    }
  }
}
